import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { DeviceDetectorService } from 'ngx-device-detector';
import { environment } from '../../environments/environment';

interface CountdownUnit {
  value: number;
  label: string;
  flipping: boolean;
}

@Component({
  selector: 'app-teaser',
  templateUrl: './teaser.component.html',
  styleUrls: ['./teaser.component.scss'],
  animations: [
    trigger('slideInOut', [
      state('in',  style({ opacity: '1', 'z-index': '100' })),
      state('out', style({ opacity: '0', 'z-index': '100' })),
      transition('in => out', animate('300ms ease-in-out')),
      transition('out => in', animate('1000ms ease-in-out')),
    ]),
  ],
})
export class TeaserComponent implements OnInit, OnDestroy {
  teaserHtml: string = environment.teaser_html;

  video_source_dash = environment.video_source_dash;
  video_source_webm = environment.video_source_webm;
  video_source_mp4  = environment.video_source_mp4;

  isMobile  = false;
  isTablet  = false;
  isDesktop = false;

  mute = true;

  // Video controls
  playing      = false;
  playingIcon  = 'pause';
  muteIcon     = 'volume_mute';

  // Show/hide animation — same pattern as gallery / phuture-login
  animationState  = 'out';
  backdropState   = 'out';
  dialogIsShown   = false;

  // Countdown
  showCountdown = false;
  countdownUnits: CountdownUnit[] = [
    { value: 0, label: 'DAYS',    flipping: false },
    { value: 0, label: 'HOURS',   flipping: false },
    { value: 0, label: 'MINUTES', flipping: false },
    { value: 0, label: 'SECONDS', flipping: false },
  ];

  private _targetDate: Date | null = null;
  private _timer: any = null;

  constructor(private deviceService: DeviceDetectorService) {
    this.isMobile  = this.deviceService.isMobile();
    this.isTablet  = this.deviceService.isTablet();
    this.isDesktop = this.deviceService.isDesktop();
  }

  ngOnInit(): void {
    // On mobile there is no hover — show card immediately
    if (this.isMobile) {
      this.animationState = 'in';
      this.backdropState  = 'in';
      this.dialogIsShown  = true;
    }

    // Countdown
    const raw: string = (environment as any).teaser_date || '';
    if (raw) {
      this._targetDate = this._parseDate(raw);
      if (this._targetDate && this._targetDate > new Date()) {
        this.showCountdown = true;
        this._tick();
        this._timer = setInterval(() => this._tick(), 1000);
      }
    }
  }

  ngOnDestroy(): void {
    if (this._timer) clearInterval(this._timer);
  }

  /** Brand logo (Media tab → brand_logo, else static asset). */
  get brandLogoUrl(): string {
    const rel = (environment as any).brand_logo;
    if (rel) {
      return `${environment.eplusSystem}/_fileDB/${rel}`;
    }
    return './assets/logo-france.jpg';
  }

  // ── Video controls ──────────────────────────────────────────────────────────

  togglePlay() {
    this.playing     = !this.playing;
    this.playingIcon = this.playing ? 'play_arrow' : 'pause';
    const v = document.getElementById('video') as HTMLVideoElement;
    if (v) { if (v.paused) v.play(); else v.pause(); }
  }

  toggleMute() {
    this.mute     = !this.mute;
    this.muteIcon = this.mute ? 'volume_mute' : 'volume_up';
  }

  // ── Mouse show/hide ─────────────────────────────────────────────────────────

  toggleBackdrop(_divName: string) {
    this.backdropState = this.backdropState === 'out' ? 'in' : 'out';
  }

  toggleShowForm(_divName: string) {
    this.animationState = this.animationState === 'out' ? 'in' : 'out';
  }

  onMouseEnter() {
    if (!this.dialogIsShown) {
      this.toggleBackdrop('overlay');
      this.toggleShowForm('teaser-center');
      this.dialogIsShown = true;
    }
  }

  @HostListener('mouseleave', ['$event'])
  onMouseLeave(event: any) {
    const screenWidth  = window.innerWidth;
    const screenHeight = window.innerHeight;
    const posX = event.pageX;
    const posY = event.pageY;
    const outOfWindow = posX < 0 || posX > screenWidth || posY < 0 || posY > screenHeight;

    if (this.dialogIsShown && outOfWindow) {
      this.toggleBackdrop('overlay');
      this.toggleShowForm('teaser-center');
      this.dialogIsShown = false;
    }
  }

  // ── Countdown ───────────────────────────────────────────────────────────────

  private _parseDate(ddmmyyyy: string): Date | null {
    const parts = ddmmyyyy.split('.');
    if (parts.length !== 3) return null;
    const [dd, mm, yyyy] = parts;
    const d = new Date(+yyyy, +mm - 1, +dd, 0, 0, 0);
    return isNaN(d.getTime()) ? null : d;
  }

  private _tick(): void {
    if (!this._targetDate) return;
    const diff = this._targetDate.getTime() - new Date().getTime();

    if (diff <= 0) {
      this.showCountdown = false;
      if (this._timer) clearInterval(this._timer);
      return;
    }

    const days    = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours   = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    const seconds = Math.floor((diff / 1000) % 60);

    [days, hours, minutes, seconds].forEach((val, i) => {
      if (val !== this.countdownUnits[i].value) {
        this.countdownUnits[i].value    = val;
        this.countdownUnits[i].flipping = true;
        setTimeout(() => (this.countdownUnits[i].flipping = false), 600);
      }
    });
  }
}
